import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { MapContainer, Marker, Popup, TileLayer, Tooltip, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import useGameBase from '../../hooks/useGameBase';
import getServerBaseUrl from '../../utils/serverBaseUrl';

const SERVER_BASE = getServerBaseUrl();

function clamp(value, min, max) {
  return Math.max(min, Math.min(max, value));
}

function toFiniteNumber(value, fallback = null) {
  const numericValue = typeof value === 'number' ? value : parseFloat(value);
  return Number.isFinite(numericValue) ? numericValue : fallback;
}

function haversineKm(lat1, lng1, lat2, lng2) {
  const earthRadiusKm = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLng / 2) ** 2;
  return earthRadiusKm * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function formatDistance(distanceKm, distanceUnit = 'km') {
  if (!Number.isFinite(distanceKm)) return 'Waiting for location';
  if (distanceUnit === 'mi') {
    const distanceMi = distanceKm * 0.621371;
    return `${distanceMi.toFixed(1)} mi away`;
  }
  return `${distanceKm.toFixed(1)} km away`;
}

function formatEta(etaMinutes, travelMode) {
  if (!Number.isFinite(etaMinutes)) return 'ETA unavailable';
  const hours = Math.floor(etaMinutes / 60);
  const minutes = etaMinutes % 60;
  const label = travelMode === 'walk' ? 'walk' : travelMode === 'cycle' ? 'cycle' : 'drive';
  if (hours > 0) return `~${hours}h ${minutes}m ${label}`;
  return `~${minutes} min ${label}`;
}

function formatSpeed(speedKmh, distanceUnit = 'km') {
  if (!Number.isFinite(speedKmh)) return distanceUnit === 'mi' ? '-- mph' : '-- kph';
  const normalizedSpeedKmh = speedKmh;
  if (distanceUnit === 'mi') {
    const speedMph = normalizedSpeedKmh * 0.621371;
    return `${speedMph.toFixed(1)} mph`;
  }
  return `${normalizedSpeedKmh.toFixed(1)} kph`;
}

function formatMarkerSummary(speedKmh, distanceKm, etaMinutes, travelMode, distanceUnit = 'km') {
  const speedText = formatSpeed(speedKmh, distanceUnit);
  const distanceText = formatDistance(distanceKm, distanceUnit);
  const etaText = formatEta(etaMinutes, travelMode);
  if (speedText && !Number.isFinite(distanceKm) && !Number.isFinite(etaMinutes)) return speedText;
  if (!Number.isFinite(distanceKm) && !Number.isFinite(etaMinutes)) return 'Waiting for location';
  if (!Number.isFinite(distanceKm)) return speedText ? `${speedText} | ${etaText}` : etaText;
  if (!Number.isFinite(etaMinutes)) return speedText ? `${speedText} | ${distanceText}` : distanceText;
  return speedText ? `${speedText} | ${distanceText} | ${etaText}` : `${distanceText} | ${etaText}`;
}

function formatAge(timestamp) {
  if (!Number.isFinite(timestamp)) return 'No update yet';
  const ageSeconds = Math.max(0, Math.round((Date.now() - timestamp) / 1000));
  if (ageSeconds < 5) return 'Updated just now';
  if (ageSeconds < 60) return `Updated ${ageSeconds}s ago`;
  const ageMinutes = Math.round(ageSeconds / 60);
  if (ageMinutes < 60) return `Updated ${ageMinutes}m ago`;
  const ageHours = Math.round(ageMinutes / 60);
  return `Updated ${ageHours}h ago`;
}

function easeInOutCubic(t) {
  return t < 0.5
    ? 4 * t * t * t
    : 1 - ((-2 * t + 2) ** 3) / 2;
}

function resolvePlayerIdentity(player, fallbackIndex = 0) {
  if (!player) return `unknown-${fallbackIndex}`;
  if (player.userId) return `user:${player.userId}`;
  if (player.socketId) return `socket:${player.socketId}`;
  if (player.username) return `name:${String(player.username).toLowerCase()}`;
  return `unknown-${fallbackIndex}`;
}

function shouldReplacePlayer(existingPlayer, nextPlayer) {
  if (!existingPlayer) return true;
  if (!nextPlayer) return false;

  // Prefer currently connected entries when reconnects leave stale duplicates.
  if (!!nextPlayer.connected && !existingPlayer.connected) return true;
  if (!!existingPlayer.connected && !nextPlayer.connected) return false;

  const existingFreshness = Number(existingPlayer.lastSeen || existingPlayer.joinedAt || 0);
  const nextFreshness = Number(nextPlayer.lastSeen || nextPlayer.joinedAt || 0);
  return nextFreshness >= existingFreshness;
}

function dedupePlayers(rawPlayers) {
  const list = Array.isArray(rawPlayers) ? rawPlayers : [];
  const byIdentity = new Map();

  list.forEach((player, index) => {
    const identity = resolvePlayerIdentity(player, index);
    const existing = byIdentity.get(identity);
    if (!existing || shouldReplacePlayer(existing, player)) {
      byIdentity.set(identity, player);
    }
  });

  return Array.from(byIdentity.values());
}

function buildParticipantIcon(label, color, isCurrentUser) {
  const border = isCurrentUser ? '#111827' : '#ffffff';
  const size = isCurrentUser ? 34 : 30;
  const fontSize = isCurrentUser ? 14 : 12;
  return L.divIcon({
    className: '',
    html: `<div style="width:${size}px;height:${size}px;border-radius:999px;background:${color};border:3px solid ${border};display:flex;align-items:center;justify-content:center;color:white;font-weight:700;font-size:${fontSize}px;box-shadow:0 8px 20px rgba(15,23,42,0.28);">${label}</div>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  });
}

function buildDestinationIcon(label = 'D') {
  const safeLabel = String(label || 'D').slice(0, 3);
  return L.divIcon({
    className: '',
    html: `<div style="width:32px;height:32px;border-radius:10px;background:#0f172a;border:3px solid #fbbf24;display:flex;align-items:center;justify-content:center;color:#fbbf24;font-weight:800;font-size:12px;box-shadow:0 8px 20px rgba(15,23,42,0.28);">${safeLabel}</div>`,
    iconSize: [32, 32],
    iconAnchor: [16, 16],
  });
}

function MapTourController({ allPoints, playerPoints, focusSeconds, overviewSeconds, onViewportChange, fullscreen = false, paused = false }) {
  const map = useMap();
  const allPointsRef = useRef(allPoints);
  const playerPointsRef = useRef(playerPoints);
  const allPointCount = Array.isArray(allPoints) ? allPoints.length : 0;
  const playerPointCount = Array.isArray(playerPoints) ? playerPoints.length : 0;

  useEffect(() => {
    allPointsRef.current = allPoints;
  }, [allPoints]);

  useEffect(() => {
    playerPointsRef.current = playerPoints;
  }, [playerPoints]);

  useEffect(() => {
    const initialAllPoints = allPointsRef.current;
    if (paused || !Array.isArray(initialAllPoints) || initialAllPoints.length === 0) return undefined;

    const safeFocusSeconds = Math.max(2, Math.min(20, Number(focusSeconds) || 5));
    const safeOverviewSeconds = Math.max(3, Math.min(30, Number(overviewSeconds) || 10));
    const overviewHoldMs = safeOverviewSeconds * 1000;
    const playerHoldMs = safeFocusSeconds * 1000;
    const focusZoom = 15;
    const moveFallbackMs = 3200;

    let cancelled = false;
    const timeoutIds = new Set();

    const clearTimers = () => {
      timeoutIds.forEach((id) => window.clearTimeout(id));
      timeoutIds.clear();
    };

    const waitMs = (ms) => new Promise((resolve) => {
      const timerId = window.setTimeout(() => {
        timeoutIds.delete(timerId);
        resolve();
      }, ms);
      timeoutIds.add(timerId);
    });

    const clearViewportTimer = () => {
      if (typeof onViewportChange === 'function') {
        onViewportChange({
          label: '',
          durationMs: 0,
          startedAt: 0,
          endsAt: 0,
        });
      }
    };

    const startHold = async (label, durationMs) => {
      if (cancelled) return false;
      const startedAt = Date.now();
      if (typeof onViewportChange === 'function') {
        onViewportChange({
          label,
          durationMs,
          startedAt,
          endsAt: startedAt + durationMs,
        });
      }
      await waitMs(durationMs);
      return !cancelled;
    };

    const waitForMoveEnd = async () => {
      if (cancelled) return false;
      await new Promise((resolve) => {
        let settled = false;

        const finish = () => {
          if (settled) return;
          settled = true;
          map.off('moveend', finish);
          resolve();
        };

        map.once('moveend', finish);

        const fallbackTimerId = window.setTimeout(() => {
          timeoutIds.delete(fallbackTimerId);
          finish();
        }, moveFallbackMs);
        timeoutIds.add(fallbackTimerId);
      });
      return !cancelled;
    };

    const animateToOverview = async () => {
      const latestAllPoints = allPointsRef.current;
      if (cancelled || !Array.isArray(latestAllPoints) || latestAllPoints.length === 0) return false;

      clearViewportTimer();
      if (latestAllPoints.length === 1) {
        map.flyTo(latestAllPoints[0], 12, { animate: true, duration: 1.2 });
      } else {
        map.flyToBounds(L.latLngBounds(latestAllPoints), {
          padding: [48, 48],
          maxZoom: 14,
          animate: true,
          duration: 1.35,
        });
      }
      return waitForMoveEnd();
    };

    const animateToPlayer = async (playerPoint) => {
      if (cancelled || !playerPoint) return false;
      clearViewportTimer();
      map.flyTo([playerPoint.lat, playerPoint.lng], focusZoom, { animate: true, duration: 1.15 });
      return waitForMoveEnd();
    };

    const runTourLoop = async () => {
      await waitMs(120);
      if (cancelled) return;
      map.invalidateSize();

      while (!cancelled) {
        const movedToOverview = await animateToOverview();
        if (!movedToOverview || cancelled) break;

        const heldOverview = await startHold('Overview', overviewHoldMs);
        if (!heldOverview || cancelled) break;

        const latestPlayers = Array.isArray(playerPointsRef.current)
          ? playerPointsRef.current.filter(
            (item) => Number.isFinite(item?.lat) && Number.isFinite(item?.lng)
          )
          : [];

        if (latestPlayers.length === 0) {
          continue;
        }

        for (const playerPoint of latestPlayers) {
          const movedToPlayer = await animateToPlayer(playerPoint);
          if (!movedToPlayer || cancelled) break;

          const focusLabel = playerPoint?.label ? `Focusing on ${playerPoint.label}` : 'Focusing player';
          const heldPlayer = await startHold(focusLabel, playerHoldMs);
          if (!heldPlayer || cancelled) break;
        }
      }
    };

    runTourLoop();

    return () => {
      cancelled = true;
      clearTimers();
      clearViewportTimer();
    };
  }, [allPointCount, focusSeconds, fullscreen, map, onViewportChange, overviewSeconds, paused, playerPointCount]);

  return null;
}

function MapManualPauseController({ enabled, paused, onPauseChange }) {
  const map = useMap();

  useEffect(() => {
    if (!enabled || typeof onPauseChange !== 'function') return undefined;

    const pauseIfNeeded = () => {
      if (!paused) {
        onPauseChange(true);
      }
    };

    const container = map.getContainer();
    const onMouseDown = () => pauseIfNeeded();
    const onTouchStart = () => pauseIfNeeded();
    const onPointerDown = () => pauseIfNeeded();

    // DOM interactions are the most reliable signal that the user is taking over the map.
    container.addEventListener('wheel', pauseIfNeeded, { passive: true });
    container.addEventListener('mousedown', onMouseDown, { passive: true });
    container.addEventListener('touchstart', onTouchStart, { passive: true });
    container.addEventListener('pointerdown', onPointerDown, { passive: true });

    return () => {
      container.removeEventListener('wheel', pauseIfNeeded);
      container.removeEventListener('mousedown', onMouseDown);
      container.removeEventListener('touchstart', onTouchStart);
      container.removeEventListener('pointerdown', onPointerDown);
    };
  }, [enabled, map, onPauseChange, paused]);

  return null;
}

function FollowMarkerController({ followedParticipant }) {
  const map = useMap();

  useEffect(() => {
    if (!followedParticipant || !followedParticipant.location) return undefined;

    const targetLatLng = L.latLng(followedParticipant.location.lat, followedParticipant.location.lng);
    const recenter = () => {
      map.panTo(targetLatLng, { animate: true, duration: 0.85 });
    };

    recenter();
    map.closePopup();

    const onMoveEnd = () => {
      const center = map.getCenter();
      if (center.distanceTo(targetLatLng) > 12) {
        recenter();
      }
    };

    map.on('dragend', onMoveEnd);
    map.on('zoomend', onMoveEnd);

    return () => {
      map.off('dragend', onMoveEnd);
      map.off('zoomend', onMoveEnd);
    };
  }, [followedParticipant, map]);

  return null;
}

function DestinationSearch({
  enabled,
  query,
  onQueryChange,
  onSubmit,
  searching,
  error,
  results,
  onSelect,
  destinations,
  pendingDestination,
  pendingDestinationName,
  onPendingDestinationNameChange,
  onAddSelected,
  onRemoveDestination,
}) {
  if (!enabled) return null;

  return (
    <div className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-700 dark:bg-slate-900">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">Destination list</h3>
          <p className="text-xs text-slate-500 dark:text-slate-400">Search, add, or remove destinations and the room updates immediately.</p>
        </div>
      </div>

      <form className="mt-3 flex gap-2" onSubmit={onSubmit}>
        <input
          value={query}
          onChange={(event) => onQueryChange(event.target.value)}
          placeholder="Search for a city, venue, or address"
          className="flex-1 rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100"
        />
        <button
          type="submit"
          disabled={searching}
          className={`rounded-xl px-3 py-2 text-sm font-semibold text-white ${searching ? 'bg-slate-400' : 'bg-sky-600 hover:bg-sky-700'}`}
        >
          {searching ? 'Searching...' : 'Find'}
        </button>
      </form>

      {error && (
        <div className="mt-3 rounded-xl bg-rose-50 px-3 py-2 text-sm text-rose-700 dark:bg-rose-950 dark:text-rose-200">
          {error}
        </div>
      )}

      {results.length > 0 && (
        <div className="mt-3 grid gap-2">
          {results.map((result) => (
            <button
              key={`${result.name}-${result.lat}-${result.lng}`}
              type="button"
              onClick={() => onSelect(result)}
              className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-left text-sm text-slate-800 hover:border-sky-300 hover:bg-sky-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100 dark:hover:border-sky-700 dark:hover:bg-slate-750"
            >
              {result.name}
            </button>
          ))}
        </div>
      )}

      {pendingDestination && (
        <div className="mt-3 rounded-2xl border border-sky-200 bg-sky-50 px-3 py-3 text-sm text-slate-700 dark:border-sky-900 dark:bg-sky-950/40 dark:text-slate-200">
          <label className="block text-xs font-medium text-slate-600 dark:text-slate-300">
            Friendly name
            <input
              value={pendingDestinationName}
              onChange={(event) => onPendingDestinationNameChange(event.target.value)}
              placeholder="Home, Office, Start line"
              className="mt-1 w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100"
            />
          </label>
          <div className="mt-1 text-xs text-slate-500 dark:text-slate-400">
            {pendingDestination.addressName}
          </div>
          <div className="mt-3 flex justify-end">
            <button
              type="button"
              onClick={onAddSelected}
              disabled={!pendingDestinationName.trim()}
              className={`rounded-xl px-3 py-2 text-sm font-semibold text-white ${pendingDestinationName.trim() ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-slate-400'}`}
            >
              Add selected
            </button>
          </div>
        </div>
      )}

      <div className="mt-3 space-y-2">
        {destinations.length === 0 && (
          <div className="rounded-2xl bg-slate-50 px-3 py-3 text-sm text-slate-500 dark:bg-slate-800 dark:text-slate-300">
            No destinations added yet.
          </div>
        )}
        {destinations.map((item, index) => (
          <div key={`${item.name}-${item.lat}-${item.lng}-${index}`} className="flex items-start justify-between gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-3 py-3 dark:border-slate-700 dark:bg-slate-950/60">
            <div>
              <div className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                {destinations.length > 1 ? `D${index + 1}` : 'Destination'}
                {item.name ? `: ${item.name}` : ''}
              </div>
              <div className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                {item.lat.toFixed(5)}, {item.lng.toFixed(5)}
              </div>
            </div>
            <button
              type="button"
              onClick={() => onRemoveDestination(index)}
              className="rounded-lg border border-slate-300 bg-white px-2.5 py-1 text-xs font-semibold text-slate-600 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
            >
              Remove
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function LocationTrackerGame({ roomId, isHost, gameState, gamePlayers = [], user = {}, socket, onGameAction, isSpectator }) {
  const { submitAction, normalizedState } = useGameBase({ gameState, gamePlayers, user, socket, roomId });
  const state = normalizedState || gameState || {};

  const mySocketId = socket?.id || user?.socketId || user?.userId || user?.id || null;
  const destinations = useMemo(() => (
    Array.isArray(state.destinations) && state.destinations.length > 0
      ? state.destinations
      : (state.destination ? [state.destination] : [])
  ), [state.destination, state.destinations]);
  const destination = destinations[0] || state.destination || {};
  const travelMode = state.travelMode || 'drive';
  const distanceUnit = state.distanceUnit || 'mi';
  const players = useMemo(() => {
    const statePlayers = Array.isArray(state.players) ? state.players : [];
    const roomPlayers = Array.isArray(gamePlayers) ? gamePlayers : [];

    // Prefer live room membership, then dedupe reconnect artifacts by stable identity.
    if (roomPlayers.length > 0) {
      return dedupePlayers(roomPlayers);
    }
    return dedupePlayers(statePlayers);
  }, [gamePlayers, state.players]);
  const participants = useMemo(() => state.participants || {}, [state.participants]);

  const [locationError, setLocationError] = useState('');
  const [trackingEnabled, setTrackingEnabled] = useState(false);
  const [destinationSearch, setDestinationSearch] = useState({
    query: '',
    results: [],
    searching: false,
    error: '',
  });
  const [mapFullscreen, setMapFullscreen] = useState(false);
  const [destinationDrafts, setDestinationDrafts] = useState(destinations);
  const [pendingDestination, setPendingDestination] = useState(null);
  const [pendingDestinationName, setPendingDestinationName] = useState('');
  const [animatedLocations, setAnimatedLocations] = useState({});
  const [speedBySocket, setSpeedBySocket] = useState({});

  const watchIdRef = useRef(null);
  const lastSentRef = useRef(null);
  const stoppedManuallyRef = useRef(false);
  const onGameActionRef = useRef(onGameAction);
  const markerAnimationRef = useRef(new Map());
  const animatedLocationsRef = useRef({});
  const speedTrackRef = useRef({});

  useEffect(() => {
    onGameActionRef.current = onGameAction;
  }, [onGameAction]);

  useEffect(() => {
    setDestinationDrafts(destinations);
  }, [destinations]);

  const dispatch = useCallback(async (action) => {
    if (typeof onGameAction === 'function') {
      onGameAction(action);
      return;
    }
    await submitAction(action);
  }, [onGameAction, submitAction]);

  const participantList = useMemo(() => {
    const orderedPlayers = Array.isArray(players) ? players : [];
    return orderedPlayers.map((player, index) => {
      const participant = participants[player.socketId] || {};
      const isCurrentUser = !!mySocketId && player.socketId === mySocketId;
      const fallbackLabel = String(player.username || player.socketId || '?').trim().charAt(0).toUpperCase() || `${index + 1}`;
      const markerLabel = String(player.icon || fallbackLabel).slice(0, 2);
      return {
        socketId: player.socketId,
        userId: player.userId || null,
        username: player.username || player.socketId,
        color: player.color || '#2563eb',
        markerLabel,
        isCurrentUser,
        destinationIndex: Number.isInteger(participant.destinationIndex) ? participant.destinationIndex : 0,
        location: participant.location || null,
        accuracyMeters: participant.accuracyMeters,
        distanceToDestinationKm: participant.distanceToDestinationKm,
        destinationDistances: Array.isArray(participant.destinationDistances) ? participant.destinationDistances : [],
        etaMinutes: participant.etaMinutes,
        lastUpdatedAt: participant.lastUpdatedAt,
        isSharing: !!participant.isSharing,
        showMarkerLabel: typeof participant.showMarkerLabel === 'boolean' ? participant.showMarkerLabel : true,
        includeInAnimation: typeof participant.includeInAnimation === 'boolean' ? participant.includeInAnimation : true,
      };
    });
  }, [mySocketId, participants, players]);

  useEffect(() => {
    const nextTrack = { ...speedTrackRef.current };

    setSpeedBySocket((currentSpeeds) => {
      let changed = false;
      const nextSpeeds = { ...currentSpeeds };
      const activeSocketIds = new Set();

      participantList.forEach((participant) => {
        const socketId = participant.socketId;
        if (!socketId) return;

        activeSocketIds.add(socketId);
        const location = participant.location;
        const lastUpdatedAt = Number(participant.lastUpdatedAt);

        if (!location || !Number.isFinite(location.lat) || !Number.isFinite(location.lng) || !Number.isFinite(lastUpdatedAt)) {
          delete nextTrack[socketId];
          if (typeof nextSpeeds[socketId] !== 'undefined') {
            delete nextSpeeds[socketId];
            changed = true;
          }
          return;
        }

        const previous = nextTrack[socketId];
        if (previous && Number.isFinite(previous.lastUpdatedAt) && lastUpdatedAt > previous.lastUpdatedAt) {
          const elapsedHours = (lastUpdatedAt - previous.lastUpdatedAt) / 3600000;
          if (elapsedHours > 0) {
            const distanceKm = haversineKm(previous.lat, previous.lng, location.lat, location.lng);
            const speedKmh = distanceKm / elapsedHours;
            if (Number.isFinite(speedKmh)) {
              const existingSpeed = nextSpeeds[socketId];
              if (!Number.isFinite(existingSpeed) || Math.abs(existingSpeed - speedKmh) > 0.05) {
                nextSpeeds[socketId] = speedKmh;
                changed = true;
              }
            }
          }
        }

        nextTrack[socketId] = {
          lat: location.lat,
          lng: location.lng,
          lastUpdatedAt,
        };
      });

      Object.keys(nextTrack).forEach((socketId) => {
        if (!activeSocketIds.has(socketId)) {
          delete nextTrack[socketId];
        }
      });

      Object.keys(nextSpeeds).forEach((socketId) => {
        if (!activeSocketIds.has(socketId)) {
          delete nextSpeeds[socketId];
          changed = true;
        }
      });

      speedTrackRef.current = nextTrack;
      return changed ? nextSpeeds : currentSpeeds;
    });
  }, [participantList]);

  useEffect(() => {
    animatedLocationsRef.current = animatedLocations;
  }, [animatedLocations]);

  useEffect(() => () => {
    markerAnimationRef.current.forEach((animationFrameId) => {
      window.cancelAnimationFrame(animationFrameId);
    });
    markerAnimationRef.current.clear();
  }, []);

  useEffect(() => {
    const activeSocketIds = new Set(participantList.map((participant) => participant.socketId));

    // Stop animations for players no longer present.
    markerAnimationRef.current.forEach((animationFrameId, socketId) => {
      if (!activeSocketIds.has(socketId)) {
        window.cancelAnimationFrame(animationFrameId);
        markerAnimationRef.current.delete(socketId);
      }
    });

    participantList.forEach((participant) => {
      const socketId = participant.socketId;
      const nextLocation = participant.location;

      if (!socketId) {
        return;
      }

      if (!nextLocation || !Number.isFinite(nextLocation.lat) || !Number.isFinite(nextLocation.lng)) {
        setAnimatedLocations((current) => {
          if (!current[socketId]) return current;
          const next = { ...current };
          delete next[socketId];
          return next;
        });
        if (animatedLocationsRef.current[socketId]) {
          const nextRef = { ...animatedLocationsRef.current };
          delete nextRef[socketId];
          animatedLocationsRef.current = nextRef;
        }
        return;
      }

      const previous = animatedLocationsRef.current[socketId];
      if (!previous || !Number.isFinite(previous.lat) || !Number.isFinite(previous.lng)) {
        const seededLocation = { lat: nextLocation.lat, lng: nextLocation.lng };
        setAnimatedLocations((current) => ({
          ...current,
          [socketId]: seededLocation,
        }));
        animatedLocationsRef.current = {
          ...animatedLocationsRef.current,
          [socketId]: seededLocation,
        };
        return;
      }

      const deltaLat = Math.abs(nextLocation.lat - previous.lat);
      const deltaLng = Math.abs(nextLocation.lng - previous.lng);
      if (deltaLat < 0.0000001 && deltaLng < 0.0000001) {
        return;
      }

      const existingAnimationFrameId = markerAnimationRef.current.get(socketId);
      if (existingAnimationFrameId) {
        window.cancelAnimationFrame(existingAnimationFrameId);
      }

      const startLat = previous.lat;
      const startLng = previous.lng;
      const startTime = performance.now();
      const durationMs = 900;

      const step = (now) => {
        const elapsed = now - startTime;
        const progress = Math.max(0, Math.min(1, elapsed / durationMs));
        const eased = easeInOutCubic(progress);
        const lat = startLat + ((nextLocation.lat - startLat) * eased);
        const lng = startLng + ((nextLocation.lng - startLng) * eased);

        setAnimatedLocations((current) => ({
          ...current,
          [socketId]: { lat, lng },
        }));
        animatedLocationsRef.current = {
          ...animatedLocationsRef.current,
          [socketId]: { lat, lng },
        };

        if (progress < 1) {
          const frameId = window.requestAnimationFrame(step);
          markerAnimationRef.current.set(socketId, frameId);
          return;
        }

        markerAnimationRef.current.delete(socketId);
      };

      const frameId = window.requestAnimationFrame(step);
      markerAnimationRef.current.set(socketId, frameId);
    });
  }, [participantList]);

  const mapParticipants = useMemo(() => participantList.map((participant) => {
    const animatedLocation = animatedLocations[participant.socketId];
    const speedKmh = speedBySocket[participant.socketId];
    if (!animatedLocation) {
      return {
        ...participant,
        speedKmh,
      };
    }
    return {
      ...participant,
      location: animatedLocation,
      speedKmh,
    };
  }), [animatedLocations, participantList, speedBySocket]);

  const mapPoints = useMemo(() => {
    const pts = [];
    destinations.forEach((item) => {
      if (Number.isFinite(item.lat) && Number.isFinite(item.lng)) {
        pts.push([item.lat, item.lng]);
      }
    });
    mapParticipants.forEach((participant) => {
      if (participant.location && Number.isFinite(participant.location.lat) && Number.isFinite(participant.location.lng)) {
        pts.push([participant.location.lat, participant.location.lng]);
      }
    });
    return pts;
  }, [destinations, mapParticipants]);

  const playerPoints = useMemo(() => mapParticipants
    .filter((participant) => (
      participant.includeInAnimation &&
      participant.location &&
      Number.isFinite(participant.location.lat) &&
      Number.isFinite(participant.location.lng)
    ))
    .map((participant) => ({
      lat: participant.location.lat,
      lng: participant.location.lng,
      label: participant.username,
    })), [mapParticipants]);

  const destinationOptions = useMemo(() => destinations.map((destination, index) => ({
    value: index,
    label: destinations.length > 1
      ? `D${index + 1}${destination.name ? `: ${destination.name}` : ''}`
      : (destination.name || 'Destination'),
  })), [destinations]);

  const focusSeconds = Number(state.playerFocusSeconds || state.config?.playerFocusSeconds || 10);
  const overviewSeconds = Number(state.overviewHoldSeconds || state.config?.overviewHoldSeconds || 10);

  const [tourViewport, setTourViewport] = useState({
    label: '',
    durationMs: 0,
    startedAt: 0,
    endsAt: 0,
  });
  const [tourProgressPct, setTourProgressPct] = useState(0);
  const [tourRemainingSeconds, setTourRemainingSeconds] = useState(0);
  const [isTourPaused, setIsTourPaused] = useState(false);
  const [followedSocketId, setFollowedSocketId] = useState(null);
  const [pauseReason, setPauseReason] = useState('');

  const activeParticipantCount = useMemo(
    () => participantList.filter((participant) => participant.isSharing && participant.location).length,
    [participantList]
  );

  const hasConfiguredDestination = destinations.length > 0;
  const followedParticipant = useMemo(
    () => mapParticipants.find((participant) => participant.socketId === followedSocketId && participant.location) || null,
    [followedSocketId, mapParticipants]
  );

  useEffect(() => {
    if (!followedSocketId) return;
    if (!followedParticipant) {
      setFollowedSocketId(null);
      setIsTourPaused(false);
      setPauseReason('');
    }
  }, [followedParticipant, followedSocketId]);

  useEffect(() => {
    if (isTourPaused || !tourViewport.durationMs || !tourViewport.startedAt) {
      setTourProgressPct(0);
      setTourRemainingSeconds(0);
      return undefined;
    }

    const tick = () => {
      const effectiveEndsAt = tourViewport.endsAt || (tourViewport.startedAt + tourViewport.durationMs);
      const elapsedMs = Math.max(0, Date.now() - tourViewport.startedAt);
      const pct = Math.max(0, Math.min(100, (elapsedMs / tourViewport.durationMs) * 100));
      const remainingMs = Math.max(0, effectiveEndsAt - Date.now());
      setTourProgressPct(pct);
      setTourRemainingSeconds(remainingMs / 1000);
    };

    tick();
    const intervalId = window.setInterval(tick, 120);
    return () => window.clearInterval(intervalId);
  }, [isTourPaused, tourViewport]);

  useEffect(() => {
    if (!isTourPaused) return;
    setTourViewport({ label: '', durationMs: 0, startedAt: 0, endsAt: 0 });
    setTourProgressPct(0);
    setTourRemainingSeconds(0);
  }, [isTourPaused]);

  const stopSharing = useCallback(async ({ clearRemote = true } = {}) => {
    if (watchIdRef.current !== null && typeof navigator !== 'undefined' && navigator.geolocation) {
      navigator.geolocation.clearWatch(watchIdRef.current);
      watchIdRef.current = null;
    }
    setTrackingEnabled(false);
    if (clearRemote) {
      try {
        await dispatch({ type: 'clearLocation' });
      } catch (error) {
        // Ignore cleanup failures.
      }
    }
  }, [dispatch]);

  const startSharing = useCallback(() => {
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      setLocationError('This browser does not support live geolocation.');
      return;
    }

    setLocationError('');
    stoppedManuallyRef.current = false;
    setTrackingEnabled(true);

    const watchId = navigator.geolocation.watchPosition(
      async (position) => {
        const nextFix = {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
          accuracyMeters: toFiniteNumber(position.coords.accuracy, null),
          heading: toFiniteNumber(position.coords.heading, null),
          timestamp: Date.now(),
        };

        const previousFix = lastSentRef.current;
        const movedKm = previousFix
          ? haversineKm(previousFix.lat, previousFix.lng, nextFix.lat, nextFix.lng)
          : Number.POSITIVE_INFINITY;
        const msSinceLastSend = previousFix ? nextFix.timestamp - previousFix.timestamp : Number.POSITIVE_INFINITY;

        if (previousFix && movedKm < 0.015 && msSinceLastSend < 5000) {
          return;
        }

        lastSentRef.current = nextFix;

        try {
          await dispatch({
            type: 'updateLocation',
            lat: nextFix.lat,
            lng: nextFix.lng,
            accuracyMeters: nextFix.accuracyMeters,
            heading: nextFix.heading,
          });
        } catch (error) {
          setLocationError('Could not send your location update to the room.');
        }
      },
      (error) => {
        setTrackingEnabled(false);
        if (watchIdRef.current !== null && navigator.geolocation) {
          navigator.geolocation.clearWatch(watchIdRef.current);
          watchIdRef.current = null;
        }
        if (error.code === error.PERMISSION_DENIED) {
          setLocationError('Location permission was denied. Enable it in the browser and try again.');
          return;
        }
        if (error.code === error.POSITION_UNAVAILABLE) {
          setLocationError('Your location could not be determined right now.');
          return;
        }
        if (error.code === error.TIMEOUT) {
          setLocationError('Timed out waiting for a location fix.');
          return;
        }
        setLocationError('Live location tracking failed.');
      },
      {
        enableHighAccuracy: true,
        maximumAge: 3000,
        timeout: 10000,
      }
    );

    watchIdRef.current = watchId;
  }, [dispatch]);

  useEffect(() => () => {
    if (watchIdRef.current !== null && typeof navigator !== 'undefined' && navigator.geolocation) {
      navigator.geolocation.clearWatch(watchIdRef.current);
    }
  }, []);

  useEffect(() => {
    return () => {
      if (!stoppedManuallyRef.current && lastSentRef.current) {
        try {
          if (typeof onGameActionRef.current === 'function') {
            onGameActionRef.current({ type: 'clearLocation' });
          }
        } catch (error) {
          // Ignore unmount cleanup failures.
        }
      }
    };
  }, []);

  useEffect(() => {
    if (!mapFullscreen) return undefined;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        setMapFullscreen(false);
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [mapFullscreen]);

  const handleSearch = useCallback(async (event) => {
    event.preventDefault();
    const query = destinationSearch.query.trim();
    if (!query) {
      setDestinationSearch((current) => ({ ...current, error: 'Enter somewhere to search for.', results: [] }));
      return;
    }

    setDestinationSearch((current) => ({ ...current, searching: true, error: '', results: [] }));
    try {
      const response = await fetch(`${SERVER_BASE}/api/whereami/geocode?q=${encodeURIComponent(query)}&limit=6`);
      const payload = await response.json();
      if (!response.ok || !payload?.ok) {
        throw new Error(payload?.error || 'Destination search failed');
      }
      const results = Array.isArray(payload.results) ? payload.results : [];
      setDestinationSearch((current) => ({
        ...current,
        searching: false,
        results,
        error: results.length > 0 ? '' : 'No places matched that search.',
      }));
    } catch (error) {
      setDestinationSearch((current) => ({
        ...current,
        searching: false,
        results: [],
        error: String(error.message || error),
      }));
    }
  }, [destinationSearch.query]);

  const mapCardClasses = mapFullscreen
    ? 'fixed inset-0 z-[240] flex h-screen w-screen flex-col rounded-none border-0 bg-white shadow-none dark:bg-slate-950'
    : 'overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-900 dark:shadow-none';
  const mapHeightClass = mapFullscreen ? 'flex-1 min-h-0 w-full bg-slate-100 dark:bg-slate-950' : 'h-[420px] w-full bg-slate-100 dark:bg-slate-950';

  const addPendingDestination = useCallback(() => {
    if (!pendingDestination || !Number.isFinite(pendingDestination.lat) || !Number.isFinite(pendingDestination.lng)) return;
    const friendlyName = pendingDestinationName.trim();
    if (!friendlyName) return;

    const nextDrafts = [...destinationDrafts, {
      name: friendlyName,
      lat: pendingDestination.lat,
      lng: pendingDestination.lng,
    }];
    setDestinationDrafts(nextDrafts);
    setPendingDestination(null);
    setPendingDestinationName('');
    setDestinationSearch((current) => ({
      ...current,
      query: '',
      results: [],
      error: '',
    }));
    dispatch({
      type: 'setDestination',
      destinations: nextDrafts,
      travelMode,
      distanceUnit,
    });
  }, [destinationDrafts, dispatch, distanceUnit, pendingDestination, pendingDestinationName, travelMode]);

  const removeDraftDestination = useCallback((indexToRemove) => {
    const nextDrafts = destinationDrafts.filter((_, index) => index !== indexToRemove);
    setDestinationDrafts(nextDrafts);
    dispatch({
      type: 'setDestination',
      destinations: nextDrafts,
      travelMode,
      distanceUnit,
    });
  }, [destinationDrafts, dispatch, distanceUnit, travelMode]);

  const updateParticipantDisplayOptions = useCallback((participantSocketId, updates = {}) => {
    if (!participantSocketId) return;
    dispatch({
      type: 'setParticipantDisplayOptions',
      socketId: participantSocketId,
      ...updates,
    });
  }, [dispatch]);

  return (
    <div className="w-full p-3 text-slate-900 dark:text-slate-100">
      <div className="mx-auto flex w-full max-w-6xl flex-col gap-4">
        <div className={mapCardClasses}>
          <div className="relative border-b border-slate-200 px-4 py-3 dark:border-slate-700">
            <div className="flex items-center justify-between gap-3 text-xs font-medium text-slate-500 dark:text-slate-400">
              <span>{destinations.length} destinations</span>
              <span>{activeParticipantCount} sharing live</span>
            </div>
            <button
              type="button"
              onClick={() => setMapFullscreen((current) => !current)}
              className="absolute left-1/2 top-1.5 h-6 -translate-x-1/2 rounded-full border border-slate-300 bg-white px-2.5 text-[11px] font-semibold text-slate-700 shadow-sm hover:border-slate-400 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700"
            >
              {mapFullscreen ? 'Exit full screen' : 'Full screen map'}
            </button>
            {isTourPaused ? (
              <div className="mt-2 flex justify-center">
                <div className="flex items-center gap-3 text-xs font-medium text-slate-600 dark:text-slate-300">
                  <span>
                    {pauseReason === 'follow' && followedParticipant
                      ? `Following ${followedParticipant.username}`
                      : 'Animations paused'}
                  </span>
                  <button
                    type="button"
                    onClick={() => {
                      setFollowedSocketId(null);
                      setIsTourPaused(false);
                      setPauseReason('');
                    }}
                    className="rounded-lg border border-sky-300 bg-sky-50 px-3 py-1.5 text-xs font-semibold text-sky-700 hover:bg-sky-100 dark:border-sky-700 dark:bg-sky-900/40 dark:text-sky-200 dark:hover:bg-sky-900/70"
                  >
                    Resume animations
                  </button>
                </div>
              </div>
            ) : tourViewport.durationMs > 0 && (
              <div className="mt-2">
                <div className="mb-1 flex items-center justify-between text-[11px] font-medium text-slate-500 dark:text-slate-400">
                  <span>{tourViewport.label}</span>
                  <span>{tourRemainingSeconds.toFixed(1)}s</span>
                </div>
                <div className="h-1.5 w-full overflow-hidden rounded-full bg-slate-200 dark:bg-slate-700">
                  <div
                    className="h-full bg-sky-500 transition-[width] duration-100 ease-linear"
                    style={{ width: `${tourProgressPct}%` }}
                  />
                </div>
              </div>
            )}
          </div>

          <div className={mapHeightClass}>
            <MapContainer
              center={Number.isFinite(destination.lat) && Number.isFinite(destination.lng) ? [destination.lat, destination.lng] : [51.5074, -0.1278]}
              zoom={12}
              style={{ height: '100%', width: '100%' }}
              zoomControl
            >
              <TileLayer
                url="https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png"
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/attributions">CARTO</a>'
              />
              <MapTourController
                allPoints={mapPoints}
                playerPoints={playerPoints}
                focusSeconds={focusSeconds}
                overviewSeconds={overviewSeconds}
                onViewportChange={setTourViewport}
                fullscreen={mapFullscreen}
                paused={isTourPaused}
              />
              <MapManualPauseController
                enabled={playerPoints.length > 0}
                paused={isTourPaused}
                onPauseChange={(paused) => {
                  if (!paused) return;
                  setFollowedSocketId(null);
                  setPauseReason('manual');
                  setIsTourPaused(true);
                }}
              />
              <FollowMarkerController followedParticipant={followedParticipant} />

              {destinations.map((item, index) => {
                if (!Number.isFinite(item.lat) || !Number.isFinite(item.lng)) return null;
                const destinationLabel = destinations.length > 1 ? `D${index + 1}` : 'D';
                return (
                  <Marker key={`${item.name}-${item.lat}-${item.lng}-${index}`} position={[item.lat, item.lng]} icon={buildDestinationIcon(destinationLabel)}>
                    <Popup>
                      <div className="text-sm">
                        <div className="font-semibold">
                          {destinations.length > 1 ? `D${index + 1}` : 'Destination'}
                          {item.name ? `: ${item.name}` : ''}
                        </div>
                        <div>{item.lat.toFixed(5)}, {item.lng.toFixed(5)}</div>
                      </div>
                    </Popup>
                  </Marker>
                );
              })}

              {mapParticipants.map((participant) => {
                if (!participant.location) return null;
                const markerTooltipText = hasConfiguredDestination
                  ? formatMarkerSummary(participant.speedKmh, participant.distanceToDestinationKm, participant.etaMinutes, travelMode, distanceUnit)
                  : `Speed: ${formatSpeed(participant.speedKmh, distanceUnit)}`;
                return (
                  <Marker
                    key={participant.socketId}
                    position={[participant.location.lat, participant.location.lng]}
                    icon={buildParticipantIcon(participant.markerLabel, participant.color, participant.isCurrentUser)}
                  >
                    {participant.showMarkerLabel && (
                      <Tooltip direction="right" permanent offset={[18, 0]} opacity={1}>
                        <span className="font-medium">{markerTooltipText}</span>
                      </Tooltip>
                    )}
                    <Popup>
                      <div className="text-sm">
                        <div className="font-semibold">{participant.username}{participant.isCurrentUser ? ' (you)' : ''}</div>
                        {hasConfiguredDestination ? (
                          <>
                            <div>{formatDistance(participant.distanceToDestinationKm, distanceUnit)}</div>
                            <div>{formatEta(participant.etaMinutes, travelMode)}</div>
                          </>
                        ) : (
                          <div className="text-slate-500 dark:text-slate-400">No destination configured yet.</div>
                        )}
                        {hasConfiguredDestination && participant.destinationDistances.length > 0 && (() => {
                          const selectedDestination = participant.destinationDistances.find((item) => item.id === participant.destinationIndex)
                            || participant.destinationDistances[participant.destinationIndex]
                            || participant.destinationDistances[0];
                          if (!selectedDestination) return null;
                          const selectedDestinationIndex = participant.destinationDistances.findIndex((item) => item === selectedDestination);
                          const safeIndex = selectedDestinationIndex >= 0 ? selectedDestinationIndex : participant.destinationIndex;
                          const destinationName = destinations.length > 1
                            ? `D${safeIndex + 1}${selectedDestination.name ? `: ${selectedDestination.name}` : ''}`
                            : (selectedDestination.name || 'Destination');
                          return (
                            <div className="mt-2 rounded-lg bg-slate-100 px-2 py-1 text-xs text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                              {destinationName}: {formatDistance(selectedDestination.distanceToDestinationKm, distanceUnit)} | {formatEta(selectedDestination.etaMinutes, travelMode)}
                            </div>
                          );
                        })()}
                        {participant.location && (
                          <button
                            type="button"
                            onClick={() => {
                              setFollowedSocketId(participant.socketId);
                              setPauseReason('follow');
                              setIsTourPaused(true);
                            }}
                            className="mt-2 rounded-lg border border-sky-300 bg-sky-50 px-2.5 py-1 text-xs font-semibold text-sky-700 hover:bg-sky-100 dark:border-sky-700 dark:bg-sky-900/40 dark:text-sky-200 dark:hover:bg-sky-900/70"
                          >
                            Follow
                          </button>
                        )}
                        <div>{formatAge(participant.lastUpdatedAt)}</div>
                      </div>
                    </Popup>
                  </Marker>
                );
              })}
            </MapContainer>
          </div>
        </div>

        <div className="flex flex-col gap-4">
          <div className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-700 dark:bg-slate-900 dark:shadow-none">
            <div className="flex items-center justify-between gap-3">
              <div>
                <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">Your location</h3>
                <p className="text-xs text-slate-500 dark:text-slate-400">Share your live position to appear on the room map.</p>
              </div>
              {!isSpectator && (
                trackingEnabled ? (
                  <button
                    type="button"
                    onClick={async () => {
                      stoppedManuallyRef.current = true;
                      await stopSharing();
                    }}
                    className="rounded-xl bg-rose-600 px-3 py-2 text-sm font-semibold text-white hover:bg-rose-700"
                  >
                    Stop sharing
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={startSharing}
                    className="rounded-xl bg-emerald-600 px-3 py-2 text-sm font-semibold text-white hover:bg-emerald-700"
                  >
                    Start sharing
                  </button>
                )
              )}
            </div>

            {isSpectator && (
              <div className="mt-3 rounded-xl bg-slate-100 px-3 py-2 text-sm text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                Spectators can view the map but cannot publish a live location.
              </div>
            )}

            {!isSpectator && locationError && (
              <div className="mt-3 rounded-xl bg-rose-50 px-3 py-2 text-sm text-rose-700 dark:bg-rose-950 dark:text-rose-200">
                {locationError}
              </div>
            )}
          </div>

          <DestinationSearch
            enabled={!!isHost}
            query={destinationSearch.query}
            onQueryChange={(value) => setDestinationSearch((current) => ({ ...current, query: value, error: '' }))}
            onSubmit={handleSearch}
            searching={destinationSearch.searching}
            error={destinationSearch.error}
            results={destinationSearch.results}
            onSelect={(result) => {
              const lat = clamp(Number(result.lat), -90, 90);
              const lng = clamp(Number(result.lng), -180, 180);
              const typedLabel = destinationSearch.query.trim();
              setPendingDestination({
                addressName: result.name,
                lat: Number(lat.toFixed(6)),
                lng: Number(lng.toFixed(6)),
              });
              setPendingDestinationName(typedLabel || result.name);
              setDestinationSearch((current) => ({ ...current, query: result.name, error: '' }));
            }}
            destinations={destinationDrafts}
            pendingDestination={pendingDestination}
            pendingDestinationName={pendingDestinationName}
            onPendingDestinationNameChange={setPendingDestinationName}
            onAddSelected={addPendingDestination}
            onRemoveDestination={removeDraftDestination}
          />

          <div className="rounded-3xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-700 dark:bg-slate-900 dark:shadow-none">
            <div className="flex items-center justify-between gap-3">
              <div>
                <h3 className="text-sm font-semibold text-slate-900 dark:text-slate-100">Participants</h3>
                <p className="text-xs text-slate-500 dark:text-slate-400">Distance and ETA are approximate and based on straight-line distance.</p>
              </div>
              <div className="text-xs font-medium text-slate-500 dark:text-slate-400">{participantList.length} total</div>
            </div>

            <div className="mt-3 space-y-2">
              {participantList.map((participant) => (
                <div
                  key={participant.socketId}
                  className={`rounded-2xl border px-3 py-3 ${participant.isCurrentUser ? 'border-sky-300 bg-sky-50 dark:border-sky-800 dark:bg-sky-950/40' : 'border-slate-200 bg-slate-50 dark:border-slate-700 dark:bg-slate-950/60'}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                        {participant.username}{participant.isCurrentUser ? ' (you)' : ''}
                      </div>
                      <div className="mt-1 text-xs text-slate-500 dark:text-slate-400">{formatAge(participant.lastUpdatedAt)}</div>
                    </div>
                    <div className="flex items-center gap-2">
                      {destinations.length > 1 && isHost && (
                        <select
                          value={participant.destinationIndex}
                          onChange={(event) => dispatch({
                            type: 'setParticipantDestination',
                            socketId: participant.socketId,
                            destinationIndex: Number(event.target.value),
                          })}
                          className="max-w-[180px] rounded-lg border border-slate-300 bg-white px-2 py-1 text-xs text-slate-700 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100"
                        >
                          {destinationOptions.map((option) => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                          ))}
                        </select>
                      )}
                      <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${participant.isSharing ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300' : 'bg-slate-200 text-slate-600 dark:bg-slate-800 dark:text-slate-300'}`}>
                        {participant.isSharing ? 'Live' : 'Offline'}
                      </span>
                    </div>
                  </div>
                  <div className="mt-3 grid gap-1 text-sm text-slate-700 dark:text-slate-300">
                    {hasConfiguredDestination ? (
                      <>
                        <div>
                          {formatDistance(participant.distanceToDestinationKm, distanceUnit)}
                          {destinations.length > 1 && destinationOptions[participant.destinationIndex] && (
                            <span className="ml-2 text-xs text-slate-500 dark:text-slate-400">
                              to {destinationOptions[participant.destinationIndex].label}
                            </span>
                          )}
                        </div>
                        <div>{formatEta(participant.etaMinutes, travelMode)}</div>
                      </>
                    ) : (
                      <div className="text-sm text-slate-500 dark:text-slate-400">No destination configured yet. Marker label shows speed.</div>
                    )}
                    <div className="mt-1 flex flex-wrap items-center gap-4 text-xs">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-slate-600 dark:text-slate-300">Show Label</span>
                        <button
                          type="button"
                          onClick={() => updateParticipantDisplayOptions(participant.socketId, { showMarkerLabel: !participant.showMarkerLabel })}
                          disabled={!isHost && !participant.isCurrentUser}
                          className={`rounded-md px-2 py-1 font-semibold ${participant.showMarkerLabel ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300' : 'bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-200'} ${(!isHost && !participant.isCurrentUser) ? 'cursor-not-allowed opacity-50' : 'hover:opacity-90'}`}
                        >
                          {participant.showMarkerLabel ? 'Yes' : 'No'}
                        </button>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-slate-600 dark:text-slate-300">Include in Animation</span>
                        <button
                          type="button"
                          onClick={() => updateParticipantDisplayOptions(participant.socketId, { includeInAnimation: !participant.includeInAnimation })}
                          disabled={!isHost && !participant.isCurrentUser}
                          className={`rounded-md px-2 py-1 font-semibold ${participant.includeInAnimation ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300' : 'bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-200'} ${(!isHost && !participant.isCurrentUser) ? 'cursor-not-allowed opacity-50' : 'hover:opacity-90'}`}
                        >
                          {participant.includeInAnimation ? 'Yes' : 'No'}
                        </button>
                      </div>
                    </div>
                    
                    {/* {Number.isFinite(participant.accuracyMeters) && (
                      <div className="text-xs text-slate-500 dark:text-slate-400">Accuracy radius: {Math.round(participant.accuracyMeters)} m</div>
                    )} */}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

LocationTrackerGame.Guide = function LocationTrackerGuide() {
  return (
    <div className="space-y-3 text-sm text-slate-700 dark:text-slate-200">
      <p>Location Tracker keeps a live room map centred on everyone sharing their position and the chosen destination list.</p>
      <ul className="list-disc pl-5 space-y-1">
        <li>Press Start sharing to publish your browser location into the room.</li>
        <li>The host can search for destinations, add several to the room, and change the ETA mode for everyone.</li>
        <li>The map automatically refits so every destination and all active participants stay visible.</li>
        <li>Distance and ETA are approximate and based on straight-line travel, not route planning, with distances shown in one decimal place.</li>
      </ul>
    </div>
  );
};